import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { createUser, updateUser, deleteUser } from '../../utils/api';

export const UsersManager = ({ users, onUpdate, onExport }) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    email: '', full_name: '', password: '', role: 'user'
  });

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ email: '', full_name: '', password: '', role: 'user' });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ email: item.email || '', full_name: item.full_name || '', password: '', role: item.role || 'user' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        const updateData = { email: formData.email, full_name: formData.full_name, role: formData.role };
        if (formData.password) updateData.password = formData.password;
        await updateUser(editingItem.id, updateData);
        toast({ title: 'Success', description: 'User updated' });
      } else {
        if (!formData.password) {
          toast({ title: 'Error', description: 'Password required for new user', variant: 'destructive' });
          return;
        }
        await createUser(formData);
        toast({ title: 'Success', description: 'User created' });
      }
      setDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await deleteUser(id);
      toast({ title: 'Success', description: 'User deleted' });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Manage Users ({users.length})</CardTitle>
          <div className="flex gap-2">
            <Button onClick={onExport}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            <Button onClick={handleAdd}><Plus className="w-4 h-4 mr-2" />Add User</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell><span className={`px-2 py-1 rounded text-xs ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>{user.role}</span></TableCell>
                <TableCell>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(user)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(user.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit User' : 'Add New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Full Name *</Label><Input required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} /></div>
              <div><Label>Email *</Label><Input required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
              <div><Label>Password {editingItem ? '(leave blank to keep current)' : '*'}</Label><Input type="password" required={!editingItem} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} /></div>
              <div><Label>Role *</Label><select required className="w-full border rounded px-3 py-2" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}><option value="user">User</option><option value="admin">Admin</option></select></div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingItem ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};